/**
 * Regras do módulo de Compras (docs/adr/0010-modulo-de-compras.md), usadas pela API e pelas telas.
 */
import { z } from 'zod';
import { costSplitInput } from './cost-center-split.ts';
import { employeeSplitInput } from './employee-split.ts';
import { RECURRENCE_FREQUENCIES } from './quotations.ts';
import { currencyCode, decimalString, uuid } from './schemas.ts';

/** Todas as situações de uma compra, na ordem do caminho. */
export const PURCHASE_STATUSES = [
  'awaiting_need_approval',
  'new',
  'in_review',
  'awaiting_info',
  'quoting',
  'awaiting_approval',
  'approved',
  'rejected',
  'ordered',
  'partially_delivered',
  'completed',
  'cancelled',
] as const;
export type PurchaseStatus = (typeof PURCHASE_STATUSES)[number];

/** Situações finais: a compra sai da fila. */
export const CLOSED_PURCHASE_STATUSES: readonly PurchaseStatus[] = [
  'rejected',
  'completed',
  'cancelled',
];

export function isClosedPurchase(status: PurchaseStatus): boolean {
  return CLOSED_PURCHASE_STATUSES.includes(status);
}

/**
 * Para onde o comprador pode levar a compra a partir de cada situação.
 * Aprovada, a compra segue pelo pedido ao fornecedor (ADR 0045), que muda a situação sozinho; antes
 * do pedido, quem compra pode cancelar a compra aprovada ou voltar à cotação (por exemplo, quando o
 * fornecedor escolhido deixou de estar apto), sempre com o motivo: a escolha é desfeita e a
 * contratação passa por nova aprovação. A aprovação anterior fica no histórico.
 * Sair de "Aguardando informações" é com o solicitante (ao responder), ou cancelando.
 */
export const BUYER_TRANSITIONS: Readonly<Record<PurchaseStatus, readonly PurchaseStatus[]>> = {
  // Enquanto o gestor do centro de custo não aprova a necessidade, a compra não está na fila.
  awaiting_need_approval: [],
  new: ['in_review', 'awaiting_info', 'quoting', 'cancelled'],
  in_review: ['awaiting_info', 'quoting', 'cancelled'],
  awaiting_info: ['cancelled'],
  quoting: ['in_review', 'awaiting_info', 'cancelled'],
  awaiting_approval: [],
  approved: ['quoting', 'cancelled'],
  rejected: [],
  ordered: [],
  partially_delivered: [],
  completed: [],
  cancelled: [],
};

/** Situações que só mudam com um comentário explicando o porquê. */
export const STATUSES_REQUIRING_COMMENT: readonly PurchaseStatus[] = ['awaiting_info', 'cancelled'];

/** A mudança de situação pede um comentário com o motivo (também a volta da compra aprovada à cotação). */
export function statusChangeNeedsComment(from: PurchaseStatus, to: PurchaseStatus): boolean {
  return STATUSES_REQUIRING_COMMENT.includes(to) || (from === 'approved' && to === 'quoting');
}

/** Quem pediu pode cancelar enquanto ninguém aprovou nem comprou. */
const REQUESTER_CAN_CANCEL: readonly PurchaseStatus[] = [
  'awaiting_need_approval',
  'new',
  'in_review',
  'awaiting_info',
  'quoting',
];

/** Quem pediu pode alterar o pedido enquanto ele é novo ou quando o comprador pediu informações. */
const REQUESTER_CAN_EDIT: readonly PurchaseStatus[] = ['new', 'awaiting_info'];

/** O comprador pode ajustar a especificação até a compra ir para aprovação. */
const BUYER_CAN_EDIT: readonly PurchaseStatus[] = ['new', 'in_review', 'awaiting_info', 'quoting'];

export interface PurchaseActor {
  /** É quem pediu. */
  requester: boolean;
  /** Tem a permissão de comprador na empresa da compra. */
  buyer: boolean;
}

/** O que a pessoa pode fazer com a compra agora. As telas mostram só esses botões. */
export function purchaseActions(status: PurchaseStatus, who: PurchaseActor) {
  const statusTargets = new Set<PurchaseStatus>(who.buyer ? BUYER_TRANSITIONS[status] : []);
  // Comprador pode cancelar enquanto a contratação espera aprovação (a aprovação é cancelada).
  if (who.buyer && status === 'awaiting_approval') statusTargets.add('cancelled');
  if (who.requester && REQUESTER_CAN_CANCEL.includes(status)) statusTargets.add('cancelled');
  return {
    edit:
      (who.requester && REQUESTER_CAN_EDIT.includes(status)) ||
      (who.buyer && BUYER_CAN_EDIT.includes(status)),
    // Só depois que o gestor do centro de custo aprova a necessidade (antes, a compra não está na fila).
    assign: who.buyer && !isClosedPurchase(status) && status !== 'awaiting_need_approval',
    answer: who.requester && status === 'awaiting_info',
    statusTargets: PURCHASE_STATUSES.filter((s) => statusTargets.has(s)),
    comment: who.requester || who.buyer,
    attach: (who.requester || who.buyer) && !isClosedPurchase(status),
    /** Ver e conduzir a cotação (C3b): só quem compra, porque mostra preços de fornecedores. */
    quote: who.buyer,
  };
}
export type PurchaseActions = ReturnType<typeof purchaseActions>;

/** Para onde a compra volta quando o solicitante responde ao pedido de informações. */
export function resumeStatusFor(current: PurchaseStatus): PurchaseStatus {
  return current === 'new' ? 'in_review' : current;
}

/**
 * Tipo da compra (o objeto da compra), para o prazo do SLA por tipo de solicitação (categoria do
 * relógio) e para os cartões da Visão geral. Quatro tipos (decisão do Fabricio de 28/10/2026):
 * - MATERIAIS: produtos comuns;
 * - BENS: produtos que precisam de cadastro no Patrimônio ou na TI;
 * - SERVICO_SIMPLES: serviço avulso (o pedido funciona como ordem de serviço);
 * - SERVICO_RECORRENTE: serviço com pagamento por período (contrato).
 * Desde 03/11/2026 (ADR 0102), quem pede escolhe o tipo na abertura, e os itens precisam combinar
 * com ele (`purchaseTypeIssues`). As solicitações antigas, sem tipo gravado, continuam com o tipo
 * deduzido dos itens (`purchaseTypeOf`): com itens de tipos diferentes, vale o mais exigente, na
 * ordem abaixo de cima para baixo. Os códigos são os que se cadastram nas regras de prazo.
 * O tipo não é a modalidade de contratação (compra nova, dentro de contrato vigente ou cobrança de
 * contrato), que é do comprador, na triagem.
 */
export const PURCHASE_TYPES = [
  'MATERIAIS',
  'BENS',
  'SERVICO_SIMPLES',
  'SERVICO_RECORRENTE',
] as const;
export type PurchaseType = (typeof PURCHASE_TYPES)[number];

export function purchaseTypeOf(
  items: readonly {
    kind: string;
    requiresAssetRegistration?: boolean | null;
    recurrenceFrequency?: string | null;
  }[],
): PurchaseType {
  const typeOf = (i: (typeof items)[number]): PurchaseType =>
    i.kind === 'service'
      ? i.recurrenceFrequency
        ? 'SERVICO_RECORRENTE'
        : 'SERVICO_SIMPLES'
      : i.requiresAssetRegistration
        ? 'BENS'
        : 'MATERIAIS';
  // O mais exigente: a ordem da lista vai do mais simples ao mais exigente.
  return items.reduce<PurchaseType>(
    (worst, i) =>
      PURCHASE_TYPES.indexOf(typeOf(i)) > PURCHASE_TYPES.indexOf(worst) ? typeOf(i) : worst,
    'MATERIAIS',
  );
}

/** Tipo que vale para a compra: o informado na abertura ou, nas antigas (nulo), o dos itens. */
export function effectivePurchaseType(
  informed: string | null | undefined,
  items: Parameters<typeof purchaseTypeOf>[0],
): PurchaseType {
  return (PURCHASE_TYPES as readonly string[]).includes(informed ?? '')
    ? (informed as PurchaseType)
    : purchaseTypeOf(items);
}

/** Por que os itens não combinam com o tipo informado (códigos traduzidos nas telas). */
export type PurchaseTypeIssueCode =
  | 'service_not_allowed'
  | 'asset_not_allowed'
  | 'recurring_not_allowed'
  | 'needs_asset'
  | 'needs_service'
  | 'needs_recurring';

export const PURCHASE_TYPE_ISSUE_MESSAGES: Record<PurchaseTypeIssueCode, string> = {
  service_not_allowed: 'Este tipo de compra não tem serviços: troque o tipo ou o item',
  asset_not_allowed:
    'Item com cadastro no Patrimônio ou na TI é de "Bens e equipamentos": troque o tipo ou o item',
  recurring_not_allowed:
    'Serviço com pagamento por período é "Serviço recorrente": troque o tipo ou o item',
  needs_asset:
    'Bens e equipamentos: marque em pelo menos um produto que precisa de cadastro no Patrimônio ou na TI',
  needs_service: 'Serviço simples: inclua pelo menos um item de serviço',
  needs_recurring:
    'Serviço recorrente: informe a periodicidade e a duração em pelo menos um serviço',
};

/**
 * Os itens combinam com o tipo informado? É a mesma regra da dedução (`purchaseTypeOf`), dita item
 * a item: o tipo informado precisa ser o mais exigente dos itens. Nada é trocado em silêncio: quem
 * pede ajusta o tipo ou os itens. Vazio = combina.
 */
export function purchaseTypeIssues(
  type: PurchaseType,
  items: Parameters<typeof purchaseTypeOf>[0],
): { path: string; code: PurchaseTypeIssueCode }[] {
  const out: { path: string; code: PurchaseTypeIssueCode }[] = [];
  const service = (i: (typeof items)[number]) => i.kind === 'service';
  const recurring = (i: (typeof items)[number]) => service(i) && !!i.recurrenceFrequency;
  const asset = (i: (typeof items)[number]) => !service(i) && !!i.requiresAssetRegistration;
  items.forEach((i, index) => {
    if ((type === 'MATERIAIS' || type === 'BENS') && service(i)) {
      out.push({ path: `items.${index}.kind`, code: 'service_not_allowed' });
    } else if (type === 'MATERIAIS' && asset(i)) {
      out.push({ path: `items.${index}.requiresAssetRegistration`, code: 'asset_not_allowed' });
    } else if (type === 'SERVICO_SIMPLES' && recurring(i)) {
      out.push({ path: `items.${index}.recurrenceFrequency`, code: 'recurring_not_allowed' });
    }
  });
  if (items.length > 0) {
    if (type === 'BENS' && !items.some(asset))
      out.push({ path: 'requestType', code: 'needs_asset' });
    if (type === 'SERVICO_SIMPLES' && !items.some(service)) {
      out.push({ path: 'requestType', code: 'needs_service' });
    }
    if (type === 'SERVICO_RECORRENTE' && !items.some(recurring)) {
      out.push({ path: 'requestType', code: 'needs_recurring' });
    }
  }
  return out;
}

/** Número que as pessoas veem: SC-0001. */
export function purchaseNumber(n: number): string {
  return `SC-${String(n).padStart(4, '0')}`;
}

// ----- Entradas -----

export const PURCHASE_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export const PURCHASE_ITEM_KINDS = ['product', 'service'] as const;

const text = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const purchaseItemInput = z
  .object({
    /** Item já existente (ao alterar o pedido). Sem id = item novo. */
    id: uuid.optional(),
    kind: z.enum(PURCHASE_ITEM_KINDS),
    description: text(500),
    quantity: decimalString(10, 4).refine(
      (v) => Number(v) > 0,
      'A quantidade precisa ser maior que zero',
    ),
    unit: text(20),
    specification: optionalText(4000),
    referenceUrl: optionalText(1000).refine(
      (v) => v == null || /^https?:\/\/\S+$/i.test(v),
      'Use um link completo, começando com http:// ou https://',
    ),
    /** Serviço: como quer o aceite (Compras confirma antes do pedido; ADR 0046). */
    acceptanceMode: z.enum(['completion', 'milestones', 'periods']).nullable().optional(),
    /** Produto: precisa de cadastro no Patrimônio ou na TI depois do aceite (sugestão). */
    requiresAssetRegistration: z.boolean().optional(),
    /**
     * Serviço recorrente: periodicidade e duração do contrato em meses. Na alçada da contratação,
     * conta o valor de 12 meses (ou o total, se a duração for menor). Os dois juntos, ou nenhum.
     */
    recurrenceFrequency: z.enum(RECURRENCE_FREQUENCIES).nullable().optional(),
    recurrenceMonths: z.number().int().min(1).max(120).nullable().optional(),
    /** Rateio do item entre centros (ADR 0094): null tira; sem o campo, fica como estava. */
    costSplit: costSplitInput.nullable().optional(),
    /** Rateio do item por colaborador: quando vem, substitui o rateio por centro. */
    employeeSplit: employeeSplitInput.nullable().optional(),
    /** Item rateado com centro de marketing: afeta o CAC? */
    affectsCac: z.boolean().nullable().optional(),
    /** Software do catálogo da TI que o item compra; sem o campo, fica como estava. */
    softwareId: uuid.or(z.literal('')).nullable().optional(),
  })
  .refine((i) => (i.recurrenceFrequency == null) === (i.recurrenceMonths == null), {
    message: 'Informe a periodicidade e a duração do contrato, em meses',
    path: ['recurrenceMonths'],
  })
  .refine((i) => i.kind === 'service' || i.recurrenceFrequency == null, {
    message: 'Só serviços são recorrentes',
    path: ['recurrenceFrequency'],
  });
export type PurchaseItemInput = z.infer<typeof purchaseItemInput>;

/**
 * Classificação financeira do item (ADR 0055): o par oficial de Dados Mestres e a origem no
 * orçamento do FP&A (versão e, se houver, o lançamento), ou a justificativa de não ter vínculo.
 * Não é controle de saldo: só rastreia de onde a compra vem.
 */
export const purchaseItemFinancialInput = z
  .object({
    itemId: uuid,
    expenseTypeId: uuid,
    classificationId: uuid,
    budgetVersionId: uuid.nullable().optional(),
    budgetExpenseId: uuid.nullable().optional(),
    noBudgetJustification: optionalText(1000),
  })
  .refine((v) => !v.budgetExpenseId || !!v.budgetVersionId, {
    message: 'Escolha a versão do orçamento do lançamento',
    path: ['budgetVersionId'],
  })
  .refine((v) => !!v.budgetVersionId !== !!v.noBudgetJustification, {
    message: 'Informe a origem no orçamento ou o motivo de comprar sem orçamento',
    path: ['noBudgetJustification'],
  })
  .refine((v) => !v.noBudgetJustification || v.noBudgetJustification.length >= 10, {
    message: 'Explique em pelo menos 10 caracteres',
    path: ['noBudgetJustification'],
  });
export const purchaseFinancialInput = z.object({
  items: z.array(purchaseItemFinancialInput).min(1).max(200),
});
export type PurchaseFinancialInput = z.infer<typeof purchaseFinancialInput>;

/** O que falta para a contratação ir para aprovação (por item). */
export type PurchaseFinancialIssue =
  | 'classification_missing'
  | 'budget_missing'
  /** A regra do banco recusou (cadastro inativo, par não permitido, orçamento de outro centro). */
  | 'classification_invalid';
export function purchaseFinancialIssues(item: {
  expenseTypeId: string | null;
  classificationId: string | null;
  budgetVersionId: string | null;
  noBudgetJustification: string | null;
}): PurchaseFinancialIssue[] {
  const out: PurchaseFinancialIssue[] = [];
  if (!item.expenseTypeId || !item.classificationId) out.push('classification_missing');
  if (!item.budgetVersionId && !item.noBudgetJustification) out.push('budget_missing');
  return out;
}

export const purchaseRequestInput = z
  .object({
    /**
     * Centro de Custo Master (ADR 0094): o gestor dele aprova a necessidade, e a área da compra é
     * a dele. Sem o campo, vale o Master do centro de custo.
     */
    masterCostCenterId: uuid.nullable().optional(),
    /** Área: só para centro de custo sem nível (legado). Com Master, a área é a dele. */
    departmentId: uuid.optional(),
    /** Centro de custo: o Master ou um centro abaixo dele (obrigatório nas solicitações novas). */
    costCenterId: uuid.nullable().optional(),
    /** "Essa compra afetará o CAC?": obrigatória nos centros de custo de marketing (cac.ts). */
    affectsCac: z.boolean().nullable().optional(),
    /** Motivo da troca da resposta do CAC (obrigatório para mudar uma resposta já dada). */
    affectsCacReason: optionalText(1000),
    /**
     * Tipo da compra, escolhido por quem pede (ADR 0102): obrigatório na abertura. Ao alterar, sem
     * o campo fica como estava; a solicitação antiga sem tipo continua com o tipo dos itens.
     */
    requestType: z.enum(PURCHASE_TYPES).nullable().optional(),
    /**
     * Contrato vigente que quem pede conhece (número ou nome), em texto. Só orienta o comprador,
     * que confere e escolhe o contrato na triagem (ADR 0102).
     */
    contractReference: optionalText(200),
    title: text(200),
    justification: text(4000),
    neededBy: z.iso.date().nullable().optional(),
    priority: z.enum(PURCHASE_PRIORITIES).default('normal'),
    estimatedAmount: decimalString(15, 4).nullable().optional(),
    estimatedCurrency: currencyCode.nullable().optional(),
    items: z.array(purchaseItemInput).min(1, 'Inclua pelo menos um item').max(200),
    /**
     * Compra em marketplace ou loja física (R1): vendedor a identificar, com o nome e o link do
     * anúncio. Só com a chave PURCHASING_MARKETPLACE ligada e até o teto.
     */
    marketplace: z.boolean().default(false),
    marketplaceSellerName: optionalText(200),
    marketplaceLink: optionalText(1000),
  })
  .refine((v) => (v.estimatedAmount == null) === (v.estimatedCurrency == null), {
    message: 'Informe o valor estimado e a moeda juntos',
    path: ['estimatedCurrency'],
  })
  .refine((v) => !v.marketplace || !!v.marketplaceSellerName, {
    message: 'Informe o nome do vendedor ou da loja',
    path: ['marketplaceSellerName'],
  })
  .refine((v) => !v.marketplace || /^https?:\/\/\S+$/.test(v.marketplaceLink ?? ''), {
    message: 'Informe o link do anúncio (começando com http:// ou https://)',
    path: ['marketplaceLink'],
  });
export type PurchaseRequestInput = z.infer<typeof purchaseRequestInput>;

/** Rateio de um item já gravado, com a resposta do CAC quando entra centro de marketing. */
export const purchaseItemSplitInput = costSplitInput.extend({
  affectsCac: z.boolean().nullable().optional(),
});
export type PurchaseItemSplitInput = z.infer<typeof purchaseItemSplitInput>;

/** Rateio por colaborador de um item já gravado. */
export const purchaseItemEmployeeSplitInput = z.object({
  employeeSplit: employeeSplitInput,
  affectsCac: z.boolean().nullable().optional(),
});

export const purchaseStatusInput = z
  .object({
    status: z.enum(PURCHASE_STATUSES),
    comment: optionalText(4000),
    /**
     * Cancelamento pelo comprador, na triagem, porque a solicitação é cobrança normal de contrato
     * vigente, que vai pelo Contas a Pagar (ADR 0102). Fica como a modalidade da compra.
     */
    contractBilling: z.boolean().optional(),
  })
  .refine((v) => !v.contractBilling || v.status === 'cancelled', {
    message: 'Cobrança de contrato só encerra a solicitação',
    path: ['contractBilling'],
  })
  .refine((v) => !STATUSES_REQUIRING_COMMENT.includes(v.status) || !!v.comment, {
    message: 'Explique o motivo num comentário',
    path: ['comment'],
  });

export const purchaseAssignInput = z.object({ buyerUserId: uuid.nullable() });

export const purchaseCommentInput = z.object({ comment: text(4000) });
